import {
  createContext,
  useRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Holds the current page's header overrides for the persistent {@link AppShell}
 * header. The title is a plain string kept in context; right-side actions are
 * rendered through a portal into a slot the header publishes, rather than stored
 * in state. Storing JSX actions in state and setting them from an effect would
 * loop — the provider wraps the routed pages, so each set re-renders the page,
 * which regenerates the inline actions element and re-fires the effect.
 */
/** A single breadcrumb between Home and the current page's leaf title. */
export interface PageCrumb {
  label: string;
  href?: string;
  /** Optional glyph before the label, for a trail whose crumbs are things. */
  icon?: ReactNode;
}

/** One array identity for "no crumbs", so the store never re-renders on it. */
const EMPTY_CRUMBS: PageCrumb[] = [];

/** What makes two trails the same trail: their labels and where they point. */
function crumbTrailKey(crumbs?: PageCrumb[]): string {
  return (crumbs ?? []).map((crumb) => `${crumb.label}\u0000${crumb.href ?? ''}`).join('\u0001');
}

interface PageChromeStore {
  title?: string;
  setTitle: (title?: string) => void;
  titleIcon?: ReactNode;
  setTitleIcon: (icon?: ReactNode) => void;
  parents: PageCrumb[];
  setParents: (parents?: PageCrumb[]) => void;
  actionsSlot: HTMLElement | null;
  setActionsSlot: (element: HTMLElement | null) => void;
  panelSlot: HTMLElement | null;
  setPanelSlot: (element: HTMLElement | null) => void;
  /** Whether the page has asked for the secondary panel to be out of the way. */
  panelHidden: boolean;
  setPanelHidden: (hidden: boolean) => void;
}

const PageChromeContext = createContext<PageChromeStore | null>(null);

/** Provides page-chrome state. Rendered once by {@link AppShell}. */
export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [title, setTitleState] = useState<string>();
  const [titleIcon, setTitleIconState] = useState<ReactNode>();
  const [parents, setParentsState] = useState<PageCrumb[]>(EMPTY_CRUMBS);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [panelSlot, setPanelSlot] = useState<HTMLElement | null>(null);
  const [panelHidden, setPanelHidden] = useState(false);

  const setTitle = useCallback((next?: string) => {
    setTitleState((prev) => (prev === next ? prev : next));
  }, []);

  const setTitleIcon = useCallback((next?: ReactNode) => {
    setTitleIconState(() => next);
  }, []);

  const setParents = useCallback((next?: PageCrumb[]) => {
    setParentsState((prev) =>
      crumbTrailKey(prev) === crumbTrailKey(next) ? prev : (next ?? EMPTY_CRUMBS),
    );
  }, []);

  const store = useMemo<PageChromeStore>(
    () => ({
      title,
      setTitle,
      titleIcon,
      setTitleIcon,
      parents,
      setParents,
      actionsSlot,
      setActionsSlot,
      panelSlot,
      setPanelSlot,
      panelHidden,
      setPanelHidden,
    }),
    [
      title,
      setTitle,
      titleIcon,
      setTitleIcon,
      parents,
      setParents,
      actionsSlot,
      panelSlot,
      panelHidden,
    ],
  );

  return <PageChromeContext.Provider value={store}>{children}</PageChromeContext.Provider>;
}

function useStore(): PageChromeStore {
  const store = useContext(PageChromeContext);
  if (!store) throw new Error('PageChrome hooks must be used within a PageChromeProvider');
  return store;
}

/**
 * Sets the current page's header title, clearing it when the calling component
 * unmounts so a previous page's title never lingers. The equality guard in
 * `setTitle` keeps a re-rendering page from looping.
 */
export function usePageChrome({
  title,
  titleIcon,
  parents,
}: {
  title?: string;
  /** Glyph before the leaf label. Stable across renders, or it resets the header each one. */
  titleIcon?: ReactNode;
  /**
   * Crumbs between the nav trail and this page's own title, outermost first —
   * a folder and its subfolder, say. Compared by label and href, so a fresh
   * array each render is cheap, but keep the icons stable.
   */
  parents?: PageCrumb[];
}): void {
  const { setTitle, setTitleIcon, setParents } = useStore();
  const parentsKey = crumbTrailKey(parents);
  /* Read through a ref so the trail's array identity, which changes every
     render, never fires the effect — only its content does. */
  const parentsRef = useRef(parents);
  parentsRef.current = parents;
  useEffect(() => {
    setTitle(title);
    setTitleIcon(titleIcon);
    setParents(parentsRef.current);
    return () => {
      setTitle(undefined);
      setTitleIcon(undefined);
      setParents(undefined);
    };
    /* `parentsKey` stands in for the trail: the array identity changes every
       render, its content rarely. */
  }, [title, titleIcon, parentsKey, setTitle, setTitleIcon, setParents]);
}

/** Read side used by the header to display the current title. */
export function usePageChromeTitle(): string | undefined {
  return useStore().title;
}

/** Read side used by the header to render the leaf's optional glyph. */
export function usePageChromeTitleIcon(): ReactNode | undefined {
  return useStore().titleIcon;
}

/** Read side used by the header to render the crumbs a page owns. */
export function usePageChromeParents(): PageCrumb[] {
  return useStore().parents;
}

/** Ref callback the header uses to publish its actions slot element. */
export function useHeaderActionsSlot(): (element: HTMLElement | null) => void {
  return useStore().setActionsSlot;
}

/**
 * Renders its children into the header's right-side actions slot. A page mounts
 * this to place page-level actions in the persistent header without passing
 * anything up the tree. When no slot has been published yet — the header
 * publishes it via a ref callback during commit, before paint — the children
 * render inline, which also keeps the actions assertable when a page is
 * rendered outside the shell (tests, Storybook).
 */
export function PageHeaderActions({ children }: { children: ReactNode }) {
  const { actionsSlot } = useStore();
  if (actionsSlot) return createPortal(children, actionsSlot);
  return <>{children}</>;
}

/** Ref callback the shell uses to publish its secondary-panel slot element. */
export function useShellPanelSlot(): (element: HTMLElement | null) => void {
  return useStore().setPanelSlot;
}

/**
 * Renders its children as the shell's secondary panel — the column between the
 * rail and the content sheet.
 *
 * A portal rather than a prop because the panel belongs to a routed page but
 * has to sit outside the sheet: it is a surface of its own beside the content,
 * not something on it, and the breadcrumb bar inside the sheet spans the
 * content alone. Falls back to rendering inline when no slot has been published,
 * the way {@link PageHeaderActions} does.
 */
export function ShellPanel({ children }: { children: ReactNode }) {
  const { panelSlot, panelHidden } = useStore();
  /* Hidden rather than unmounted while a page has the panel out of the way,
     so the tree inside keeps its open folders and scroll for when it returns.
     `contents` comes off with it: an author `display` would beat the
     `hidden` attribute's own. */
  const content = (
    <div className={panelHidden ? undefined : 'contents'} hidden={panelHidden}>
      {children}
    </div>
  );
  if (panelSlot) return createPortal(content, panelSlot);
  return content;
}

/**
 * Takes the shell's secondary panel out of the way while `hidden` is true —
 * the content sheet then runs to the rail, which is how a page gives one of
 * its own surfaces the whole column. Restored when the flag drops or the
 * calling page unmounts, so a route change mid-maximize never leaves the next
 * page without its panel.
 */
export function useShellPanelHidden(hidden: boolean): void {
  const { setPanelHidden } = useStore();
  useEffect(() => {
    setPanelHidden(hidden);
    return () => setPanelHidden(false);
  }, [hidden, setPanelHidden]);
}
